import UploadItem from "@/shared/models/UploadItem";
import { useUploadDocumentByUrl } from "@/features/chat-management/api/useUploadDocumentByUrl";
import { useEffect } from "react";
import { errorNotification } from "@/shared/utils/Notifications";
import {
  ExternalLink as Link,
  Link2Icon,
  Link2OffIcon,
  Loader2,
  X,
} from "lucide-react";

interface AddedWebpageProps {
  item: UploadItem;
  onItemRemove: (id: number) => void;
  onItemStatusUpdate: (item: UploadItem) => void;
}

export function AddedWebpage({
  item,
  onItemRemove,
  onItemStatusUpdate,
}: AddedWebpageProps) {
  const uploadFailedMessage = "Upload failed";

  const {
    uploadDocument,
    isLoading: isUploading,
    isFailed: isUploadFailed,
  } = useUploadDocumentByUrl();

  const updateItemStatus = (
    status: typeof item.status,
    uploadedId?: string,
    error?: string,
  ) => {
    item.status = status;
    if (uploadedId) item.uploadedId = uploadedId;
    if (error) item.error = error;
    onItemStatusUpdate(item);
  };

  useEffect(() => {
    const upload = async () => {
      if (item.url && !item.uploadedId) {
        try {
          updateItemStatus("uploading");
          const uploadedId = (await uploadDocument(item.url)).id;
          if (uploadedId) updateItemStatus("complete", uploadedId);
          else updateItemStatus("error", undefined, uploadFailedMessage);
        } catch (_error) {
          updateItemStatus("error", undefined, uploadFailedMessage);
          errorNotification(item.name, uploadFailedMessage);
        }
      }
    };

    if (item.status === "toProcess") {
      upload();
    } else if (item.uploadedId && item.status !== "complete") {
      updateItemStatus("complete", item.uploadedId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item]);

  const getBackgroundColor = () => {
    if (isUploadFailed || item.status === "error") return "bg-red-50";
    return "bg-purple-50";
  };

  return (
    <div
      key={item.id}
      className={`flex items-center justify-between ${getBackgroundColor()} text-purple-700 px-4 py-2 rounded-lg`}
    >
      <div className="flex items-center gap-2 min-w-0">
        {isUploading || item.status === "uploading" ? (
          <Loader2 className="animate-spin h-4 w-4 flex-shrink-0" />
        ) : isUploadFailed || item.status === "error" ? (
          <Link2OffIcon className="h-4 w-4 flex-shrink-0" />
        ) : (
          <Link2Icon className="h-4 w-4 flex-shrink-0" />
        )}
        <a
          href={item.url}
          target="_blank"
          rel="noopener noreferrer"
          className="hover:text-purple-900 flex items-center gap-1"
        >
          <span className="break-all">{item.url}</span>
          <Link className="h-3 w-3 flex-shrink-0" />
        </a>
      </div>
      <button
        onClick={() => onItemRemove(item.id)}
        className="text-purple-500 hover:text-purple-700 ml-2 flex-shrink-0"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
